import React, { useState, useCallback, useRef, ChangeEvent } from "react";
import { styled } from "../stitches.config";
import { AnyBlock, ImageBlock as ImageBlockType, User } from "../types";
import {
  capImageSize,
  createPreviewDataURL,
  upload,
} from "../utils/fileUpload";
import Icon from "../components/Icon";
import { Button } from "../components/Button";
import { BlockInput } from "./WebBlock";

function ImageBlock({
  block,
  updateBlock,
  madeBy,
}: {
  block: ImageBlockType;
  updateBlock: (block: Partial<AnyBlock>) => void;
  madeBy?: User;
}) {
  const [error, setError] = useState<string | null>();

  const updateValue = useCallback(
    (value?: string) => {
      updateBlock({ id: block.id, value });
    },
    [block, updateBlock]
  );

  const onSelectFile = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;

      if (!files || files?.length === 0) {
        setError("No file selected, try again");
        return;
      }

      const file = files[0];

      if (!file.type.startsWith("image")) {
        setError("Selected file is not an image, try again");
        return;
      }

      if (error) {
        setError(null);
      }

      const image = new Image();

      image.addEventListener("load", () => {
        updateValue(createPreviewDataURL(image));

        capImageSize(image, file)
          .then(upload)
          .then((url) => updateValue(url))
          .catch((err) => {
            setError(err);
          });
      });

      image.src = URL.createObjectURL(file);
    },
    [error, setError, updateValue]
  );

  const fileInputRef = useRef<HTMLInputElement>(null);
  const isSmall = block.size.width < 10 || block.size.height < 8;
  const src = block.value?.replace(
    "https://makespace1.s3.amazonaws.com",
    "https://file.sprout.place"
  );

  return (
    <>
      {block.value ? (
        <ImageContainer>
          <Img
            sticker={block.sticker}
            src={src}
            // drag block not image
            draggable={false}
            alt={
              (block.madeBy &&
                madeBy?.name &&
                `Image added by ${madeBy.name}`) ??
              ""
            }
          />
        </ImageContainer>
      ) : (
        <UploadDialog>
          <ImageUploadContent>
            {!isSmall && (
              <Icon
                style={{ transform: "scale(2.0)", height: 30 }}
                icon="ImgBlock"
              ></Icon>
            )}
            {!isSmall && (
              <p>
                Tip: You can drag and drop images onto the canvas at any time!
              </p>
            )}
            <input
              type="file"
              style={{ display: "none" }}
              onChange={onSelectFile}
              ref={fileInputRef}
            />
            <Button
              color="brown"
              icon="Upload"
              style={{ width: "100%" }}
              onClick={() => {
                fileInputRef.current?.click();
              }}
            >
              Browse Files...
            </Button>
            <div>{error}</div>

            <BlockInput
              placeholder="Paste Image URL here"
              onMouseDown={(e) => e.stopPropagation()} // prevent moving/selecting block
              onPaste={(ev) => {
                ev.stopPropagation();
                const paste = ev.clipboardData.getData("text");
                updateValue(paste);
              }}
            />
          </ImageUploadContent>
        </UploadDialog>
      )}
    </>
  );
}

export default ImageBlock;

// Fixes bug with <img> object-fit not working well in grid-template
const ImageContainer = styled("div", {
  width: "100%",
  height: "100%",
  overflow: "hidden",
});
const ImageUploadContent = styled("div", {
  width: "100%",
  height: "100%",
  maxWidth: 300,
  maxHeight: 150,

  margin: "auto",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "center",
});

const UploadDialog = styled("div", {
  width: "calc(100% - 1rem)",
  height: "calc(100% - 1rem)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "spaceBetween",
  margin: "$2",
  padding: "$2",
  border: " 1px dashed $paper10",
  overflow: "hidden",
});

const Img = styled("img", {
  width: "100%",
  height: "100%",
  objectFit: "contain",
  variants: {
    sticker: {
      true: { objectFit: "cover" },
      false: { objectFit: "contain" },
    },
  },
});
